import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Saint-Gély-du-Fesc' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Saint-Gély-du-Fesc',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Saint-Gély-du-Fesc' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Saint-Gély-du-Fesc. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous sur les baies grand format des villas de Saint-Gély-du-Fesc ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, les villas de Saint-Gély-du-Fesc sont souvent équipées de grandes baies coulissantes. Nous maîtrisons le remplacement des roulettes sur tous les formats, y compris les baies à galets renforcés pour vantaux lourds. Devis gratuit.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les motorisations de volet et store à Saint-Gély ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Nous réparons et remplaçons les moteurs de volets roulants et de stores. La motorisation est très demandée dans ce secteur où les équipements domotiques sont courants. Intervention sous 24-48h.',
      },
    },
  ],
};

export default function SaintGelyDuFescPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante St-Gély | DFPRO"
        description="Baie coulissante grand format, volet roulant motorisé en panne à Saint-Gély-du-Fesc ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-saint-gely-du-fesc/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Saint-Gély-du-Fesc"
        subtitle="Villas contemporaines, baies grand format, volets motorisés : nos techniciens réparent vos menuiseries à Saint-Gély-du-Fesc. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Baie grand format ou volet motorisé en panne ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens interviennent à Saint-Gély-du-Fesc et dans le nord de Montpellier, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Saint-Gély-du-Fesc, au nord de Montpellier, compte de nombreuses villas contemporaines avec de grandes baies vitrées et des équipements motorisés. Nous intervenons sur ces menuiseries haut de gamme. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/stgely-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Saint-Gély-du-Fesc"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante grand format : roulettes renforcées</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les villas de Saint-Gély-du-Fesc sont équipées de baies coulissantes de grandes dimensions. Les vantaux lourds sollicitent davantage les roulettes. Quand la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">devient dure</Link>, il faut changer les galets avant que le rail ne s'abîme.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes renforcées</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant motorisé et domotique</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les équipements domotiques sont courants à Saint-Gély-du-Fesc. <Link to="/volet-roulant-electrique-herault/" className="text-accent-500 font-semibold hover:underline">Volet électrique</Link> qui ne répond plus à la télécommande, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> silencieux mais qui ne tourne plus : nous diagnostiquons la panne.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Motorisation</Link>, <Link to="/reparation-store-herault/" className="text-accent-500 font-semibold hover:underline">stores</Link>, <Link to="/motorisation-store-herault/" className="text-accent-500 font-semibold hover:underline">motorisation store</Link>, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/stgely-volet-02.jpg"
              alt="Réparation de volet roulant motorisé à Saint-Gély-du-Fesc"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/stgely-vitrage-03.webp"
              alt="Remplacement de vitrage à Saint-Gély-du-Fesc"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage et fenêtre : isolation thermique</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> pour <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> performant, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>, <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Saint-Gély-du-Fesc sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Saint-Gély-du-Fesc : centre, quartiers résidentiels, lotissements. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-grabels/" className="text-accent-500 font-semibold hover:underline">Grabels</Link>, Les Matelles. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}